import { Beneficiario, BeneficiarioPageResponse, CentroTrabajo, CentroTrabajoPageResponse } from '../../model/beneficiario/beneficiario';

/**
 * Filtros estructurados para búsqueda de beneficiarios.
 * Migrado de BuscadorBeneficiariosViewImpl.getDTO() del legacy.
 * 
 * Cada campo genera un WHERE independiente (AND):
 * - tipoIdentificacion: EQUAL operator
 * - Todos los demás: LIKE operator (% + valor + %)
 */
export interface BeneficiarioFiltroEstructurado {
  tipoIdentificacion?: string;
  numeroIdentificacion?: string;
  contrato?: string;
  nombreComercial?: string;
  nombre1?: string;
  nombre2?: string;
  apellido1?: string;
  apellido2?: string;
}

/**
 * Puerto Abstracto: BeneficiarioRepository
 * 
 * Define el contrato para operaciones con Beneficiarios (Clientes).
 * Migrado del legacy GWT BuscadorBeneficiariosActivity.
 * 
 * Arquitectura Hexagonal:
 * - Este puerto está en el dominio
 * - HttpBeneficiarioRepository (adaptador) lo implementa en infraestructura
 * 
 * @author Ingeneo - Migración GWT
 */
export abstract class BeneficiarioRepository {
  
  /**
   * Busca beneficiarios por filtro de texto
   * El filtro busca en: identificación, nombre comercial, nombre completo
   * @param filtro Texto de búsqueda (mínimo 3 caracteres)
   * @param limit Máximo de resultados (por defecto 50)
   */
  abstract buscarPorFiltro(filtro: string, limit?: number): Promise<Beneficiario[]>;
  
  /**
   * Busca beneficiarios con filtros estructurados y paginación keyset/cursor
   * (ver skill acceso-datos-oracle §7 — nunca OFFSET).
   * Migrado de Tpsc_beneficiarioPersistenceServicesImpl.consultar() + getWhereClauses()
   *
   * Legacy: Cada campo se envía como WHERE independiente con AND.
   * - tipoIdentificacion → EQUAL
   * - Los demás → LIKE (%valor%)
   *
   * @param filtros Filtros estructurados (cada campo se busca independientemente)
   * @param cursor Cursor opaco devuelto por la página anterior (null = primera página)
   * @param size Tamaño de página
   * @param direction 'NEXT' | 'PREVIOUS' | 'LAST'
   */
  abstract buscarConFiltrosEstructurados(
    filtros: BeneficiarioFiltroEstructurado,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<BeneficiarioPageResponse>;

  /**
   * Obtiene un beneficiario por su consecutivo
   * @param nmconsecutivo Identificador único del beneficiario
   */
  abstract obtenerPorConsecutivo(nmconsecutivo: number): Promise<Beneficiario | null>;

  /**
   * Busca centros de trabajo con paginación keyset/cursor unificada (1 sola
   * llamada). Retorna content + totalElements.
   *
   * @param nmconsecutivoBenef Consecutivo del beneficiario (0 = todos)
   * @param filtro Filtro por nombre/código de centro
   * @param codigo Filtro por NMCONSECUTIVO (sin wildcards, como legacy)
   * @param cursor Cursor opaco devuelto por la página anterior (null = primera página)
   * @param size Tamaño de página
   * @param direction 'NEXT' | 'PREVIOUS' | 'LAST'
   */
  abstract buscarCentrosTrabajoPaginado(
    nmconsecutivoBenef: number,
    filtro: string | undefined,
    codigo: string | undefined,
    cursor: string | null,
    size: number,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST'
  ): Promise<CentroTrabajoPageResponse>;
}
